import type { QueryKey, UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useClient } from '../../../app/api';
import type { PanelSettingsDto } from '../../../app/api/generated/client';
import { showToast } from '../../../app/utils/toastUtils';
import { mutationKeys } from '../mutation-keys';
import { queryKeys } from '../query-keys';

type Context = { previousSettings?: PanelSettingsDto };

interface Options {
    mutationFn: (clientId: number, selected: boolean) => Promise<PanelSettingsDto>;
    settingsQueryKey: (partnerUuid?: string) => QueryKey;
}

/**
 * Bulk select/clear wiring for a liquidity cashflow panel: the checkbox selects every item when checked and clears the
 * selection when unchecked, in a single request.
 *
 * `onMutate` optimistically flips the `enabled` flag on every selectable (non enforce-disabled) item so the control
 * reacts instantly, `onError` rolls back, and `onSuccess` seeds the cache with the server-returned panel and refetches
 * the settings plus the server-computed calculation and overview.
 *
 * It shares the single settings `mutationKey` so `useLiquidityCalculationData` can dim the table/graph while it is in flight.
 */
export const useLiquiditySelectAllMutation = ({ mutationFn, settingsQueryKey }: Options): UseMutationResult<PanelSettingsDto, AxiosError, boolean, Context> => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data: client } = useClient();
    const partnerUuid = client?.partnerUuid;
    const settingsKey = settingsQueryKey(partnerUuid);

    return useMutation({
        mutationKey: mutationKeys.updateLiquiditySettings(),
        mutationFn: (selected: boolean) => mutationFn(client!.id, selected),
        onMutate: async (selected) => {
            await queryClient.cancelQueries({ queryKey: settingsKey });
            const previousSettings = queryClient.getQueryData<PanelSettingsDto>(settingsKey);

            if (previousSettings) {
                const nextSettings: PanelSettingsDto = {
                    ...previousSettings,
                    itemsSettings: previousSettings.itemsSettings.map((item) => (item.enforceDisable ? item : { ...item, enabled: selected })),
                };
                queryClient.setQueryData<PanelSettingsDto>(settingsKey, nextSettings);
            }

            return { previousSettings };
        },
        onError: (_error, _variables, context) => {
            if (context?.previousSettings) queryClient.setQueryData(settingsKey, context.previousSettings);
            showToast('error', true, t('liquidityCalculatorSettings.updateError'));
        },
        onSuccess: (updatedSettings) => {
            if (updatedSettings) queryClient.setQueryData<PanelSettingsDto>(settingsKey, updatedSettings);
            queryClient.invalidateQueries({ queryKey: settingsKey });
            queryClient.invalidateQueries({ queryKey: queryKeys.liquidityOverview(partnerUuid) });
            queryClient.invalidateQueries({ queryKey: queryKeys.liquidityCalculatorCalculation(partnerUuid) });
        },
    });
};
